import { useEffect, useId, useRef, useState } from 'react';
import {
  messageDeliveryStatus,
  messageTrigger,
  type DeliveryInspection,
  type DeliveryInspectionQuery,
} from '@dental-staffing-platform/shared';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Row,
  Select,
  SkeletonList,
  Stack,
} from '@dental-staffing-platform/ui';
import { deliveryAdminApi } from '../lib/message-delivery-client.js';
import { useAsync } from '../lib/useAsync.js';
import { humanize } from '../lib/format.js';

const timestamp = (value: string | null) =>
  value ? new Date(value).toLocaleString() : 'Not recorded';

function DeliveryDetail({ id, close }: { id: string; close: () => void }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), []);
  const state = useAsync(() => deliveryAdminApi.get(id), [id]);
  const data = state.data;
  return (
    <Card>
      <Row style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <h2 ref={heading} tabIndex={-1} style={{ margin: 0 }}>
          Delivery details
        </h2>
        <Button variant="secondary" onClick={close}>
          Close details
        </Button>
      </Row>
      {state.loading ? (
        <SkeletonList count={3} />
      ) : state.error || !data ? (
        <div role="alert">
          <p>{state.error ?? 'Delivery record could not be loaded.'}</p>
          <Button onClick={state.reload}>Retry details</Button>
        </div>
      ) : (
        <>
          <h3>{data.title}</h3>
          <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{data.body}</p>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(100px, 1fr) minmax(0, 2fr)',
              gap: 10,
              overflowWrap: 'anywhere',
            }}
          >
            {(
              [
                ['Delivery ID', data.id],
                ['Recipient user ID', data.recipientUserId],
                ['Booking ID', data.bookingId],
                ['Shift ID', data.shiftId],
                ['Template ID', data.templateId],
                ['Inbox notification ID', data.notificationId],
                ['Event', humanize(data.trigger)],
                ['Record status', humanize(data.status)],
                ['Channel', data.channel ? humanize(data.channel) : null],
                [
                  'Notification status',
                  data.notificationStatus ? humanize(data.notificationStatus) : null,
                ],
                ['Attempts', data.attemptCount === null ? null : String(data.attemptCount)],
                ['Created', timestamp(data.createdAt)],
                ['Updated', timestamp(data.updatedAt)],
                ['Next attempt', timestamp(data.nextAttemptAt)],
                ['Sent', timestamp(data.sentAt)],
                ['Read', timestamp(data.readAt)],
              ] satisfies [string, string | null][]
            ).map(([label, value]) => (
              <div key={label} style={{ display: 'contents' }}>
                <dt className="dsp-muted">{label}</dt>
                <dd style={{ margin: 0 }}>{value ?? 'Not recorded'}</dd>
              </div>
            ))}
          </dl>
          {data.failureNotice && (
            <p role="status" className="dsp-field__error">
              {data.failureNotice}
            </p>
          )}
          <p className="dsp-muted">
            Generated event history is read-only. A sent record does not confirm that the recipient
            opened the message. Booking access is checked separately in the staffing workspace.
          </p>
        </>
      )}
    </Card>
  );
}

export function MessageDeliveries() {
  const fieldId = useId();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<DeliveryInspectionQuery['status']>();
  const [trigger, setTrigger] = useState<DeliveryInspectionQuery['trigger']>();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState<DeliveryInspectionQuery>({ page: 1, pageSize: 20 });
  const state = useAsync(() => deliveryAdminApi.list({ ...filter, page }), [filter, page]);
  const apply = () => {
    setPage(1);
    setSelected(null);
    setFilter({
      page: 1,
      pageSize: 20,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(status ? { status } : {}),
      ...(trigger ? { trigger } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    });
  };
  return (
    <Stack gap={16}>
      <PageHeader
        title="Message deliveries"
        subtitle="Inspect event messages and delivery outcomes for your organization."
      />
      <Card>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
        >
          <Row gap={12} style={{ flexWrap: 'wrap', alignItems: 'end' }}>
            <Field label="Search deliveries" htmlFor={`${fieldId}-search`}>
              <Input
                id={`${fieldId}-search`}
                type="search"
                maxLength={100}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Message, recipient, or booking ID"
              />
            </Field>
            <Field label="Record status" htmlFor={`${fieldId}-status`}>
              <Select
                id={`${fieldId}-status`}
                value={status ?? ''}
                onChange={(event) =>
                  setStatus(
                    event.target.value ? messageDeliveryStatus.parse(event.target.value) : undefined
                  )
                }
              >
                <option value="">All statuses</option>
                {messageDeliveryStatus.options.map((value) => (
                  <option key={value} value={value}>
                    {humanize(value)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Event" htmlFor={`${fieldId}-event`}>
              <Select
                id={`${fieldId}-event`}
                value={trigger ?? ''}
                onChange={(event) =>
                  setTrigger(
                    event.target.value ? messageTrigger.parse(event.target.value) : undefined
                  )
                }
              >
                <option value="">All events</option>
                {messageTrigger.options.map((value) => (
                  <option key={value} value={value}>
                    {humanize(value)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Created from (UTC)" htmlFor={`${fieldId}-from`}>
              <Input
                id={`${fieldId}-from`}
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </Field>
            <Field label="Created through (UTC)" htmlFor={`${fieldId}-to`}>
              <Input
                id={`${fieldId}-to`}
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </Field>
            <Button type="submit">Apply filters</Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setSearch('');
                setStatus(undefined);
                setTrigger(undefined);
                setFrom('');
                setTo('');
                setPage(1);
                setSelected(null);
                setFilter({ page: 1, pageSize: 20 });
              }}
            >
              Reset filters
            </Button>
          </Row>
        </form>
      </Card>
      {state.loading ? (
        <SkeletonList count={4} />
      ) : state.error || !state.data ? (
        <Card>
          <div role="alert">
            <p>{state.error ?? 'Delivery records could not be loaded.'}</p>
            <Button onClick={state.reload}>Retry deliveries</Button>
          </div>
        </Card>
      ) : state.data.items.length === 0 ? (
        <EmptyState
          title="No delivery records"
          description="No event messages match these filters. Records are created when staffing events occur."
        />
      ) : (
        <>
          <Stack gap={8}>
            {state.data.items.map((delivery: DeliveryInspection) => (
              <Card key={delivery.id}>
                <Row
                  gap={12}
                  style={{
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    overflowWrap: 'anywhere',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <strong>{delivery.title}</strong>
                    <p className="dsp-muted">
                      {humanize(delivery.trigger)} · {timestamp(delivery.createdAt)}
                    </p>
                    <p style={{ overflowWrap: 'anywhere' }}>
                      Recipient: {delivery.recipientUserId}
                      {delivery.bookingId ? ` · Booking: ${delivery.bookingId}` : ''}
                    </p>
                  </div>
                  <Row gap={8} style={{ flexWrap: 'wrap' }}>
                    <Badge
                      tone={
                        delivery.status === 'failed'
                          ? 'danger'
                          : delivery.status === 'sent'
                            ? 'success'
                            : 'neutral'
                      }
                    >
                      {humanize(delivery.status)}
                    </Badge>
                    <Button variant="secondary" onClick={() => setSelected(delivery.id)}>
                      View delivery
                    </Button>
                  </Row>
                </Row>
              </Card>
            ))}
          </Stack>
          <Pagination
            {...state.data}
            itemLabel="delivery records"
            onPageChange={(value) => {
              setPage(value);
              setSelected(null);
            }}
          />
        </>
      )}
      {selected && <DeliveryDetail key={selected} id={selected} close={() => setSelected(null)} />}
    </Stack>
  );
}
